import { useCallback } from 'react';
import { AnalyticEvent, AnalyticEventsDataTypes, AnalyticsEventMap, CustomEventName } from '@sweatpals/shared';
import { useFirebaseAnalytics } from './use-firebase-analytics.hook';
import { useAmplitudeAnalytics } from './use-amplitude-analytics.hook';
import { useGTMEventTracking } from './use-gtm-event-tracking.hook';

/**
 * A hook that provides a unified analytics interface by combining Firebase and Amplitude analytics.
 *
 * This hook allows you to log events to both Firebase and Amplitude analytics services
 * simultaneously and set user IDs across both platforms.
 *
 * @example
 * ```tsx
 * const { logEventTyped, setUserIdExternally } = useUnifiedAnalytics();
 *
 * // Log an event
 * logEventTyped('sign_up', { method: 'email' });
 *
 * // Set user ID
 * setUserIdExternally('user-123');
 * ```
 *
 * @returns An object containing functions to log events and set user IDs.
 *
 * @note This hook can only be used within components that have access to the Firebase and Amplitude
 * analytics context providers.
 */
export const useUnifiedAnalytics = <TEvents extends AnalyticsEventMap = AnalyticEventsDataTypes>() => {
	const { logEventTyped: logFirebaseEvent, setUserIdExternally: setFirebaseUserIdExternally } = useFirebaseAnalytics<TEvents>();
	const { logEventTyped: logAmplitudeEvent, setUserIdExternally: setAmplitudeUserIdExternally } = useAmplitudeAnalytics<TEvents>();
	const { logEventTyped: logGtmEvent, setUserDataExternaly: setGtmUserDataExternaly } = useGTMEventTracking<TEvents>();

	const logEventTyped = useCallback(
		<T extends CustomEventName<TEvents>>(
			event: T,
			metadata: AnalyticEvent<T, TEvents>
		) => {
		try {
			metadata = {
				platform: 'web',
				projectPart: 'landing',
				...metadata,
			};

			logFirebaseEvent(event, metadata);
			logAmplitudeEvent(event, metadata);
      logGtmEvent(event, metadata);

			console.info('[Analytics] Event logged: ', { event, metadata });
		} catch (e) {
			console.error('[Analytics] Error while logging event', e);
		}
	}, []);

	const setUserIdExternally = useCallback((userId: string) => {
		setFirebaseUserIdExternally?.(userId);
		setAmplitudeUserIdExternally?.(userId);
	}, [setFirebaseUserIdExternally, setAmplitudeUserIdExternally]);

	return {
		logEventTyped,
		setUserIdExternally
	};
};
